import { itemsCol } from '../shared/array-types';
import { objectRequest } from '../shared/object-request';

type Definition = Record<string, unknown>;
type Waiter = (value: Definition | null) => void;
type Catalog = {
    held: Map<string, Definition | null>;
    pending: Map<string, Promise<Definition | null>>;
    queue: Map<string, Waiter>;
};
type CatalogWindow = Window & { __ftReferenceCatalog?: Catalog };

function record(value: unknown): Definition | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Definition : null;
}

// Several aggregates contain list-display. Share reads through the page, never
// import a stateful owner from the websocket or object-card bundle.
function pageCatalog(): Catalog {
    return (window as CatalogWindow).__ftReferenceCatalog ??= { held: new Map(), pending: new Map(), queue: new Map() };
}

async function flush(catalog: Catalog): Promise<void> {
    const queue = [...catalog.queue];
    catalog.queue.clear();
    for (let start = 0; start < queue.length; start += 64) {
        const batch = queue.slice(start, start + 64);
        try {
            const base = typeof BASE_URL === 'string' ? BASE_URL : '/';
            const objects = await objectRequest(async signal => {
                const response = await fetch(base + 'options/api/object_inc.php?ids='
                    + encodeURIComponent(batch.map(([id]) => id).join(',')) + '&cold=0', { credentials: 'same-origin', signal });
                if (!response.ok) throw new Error(`Reference catalog read failed (${response.status})`);
                const body = record(await response.json());
                const objects = record(body?.objects);
                if (!objects) throw new Error('Reference objects missing');
                return objects;
            });
            const definitions: Definition = {};
            for (const [id, value] of Object.entries(objects)) {
                const object = record(value);
                const envelope = record(object?._entity_base);
                const saved = record(envelope?.base) ?? object;
                if (!saved || String(itemsCol(saved, 'item_id') ?? '').toUpperCase() !== id.toUpperCase()) continue;
                Object.assign(definitions, record(saved.catalog));
                definitions[id.toUpperCase()] = saved;
            }
            for (const [id, value] of Object.entries(definitions)) {
                const definition = record(value);
                if (!definition || String(itemsCol(definition, 'item_id') ?? '').toUpperCase() !== id.toUpperCase()) continue;
                const full = { ...definition };
                // Runes can resolve sibling definitions from this read too. Keep
                // the index non-enumerable so render walks don't copy it per row.
                Object.defineProperty(full, 'catalog', { value: definitions, configurable: true });
                catalog.held.set(id.toUpperCase(), full);
            }
            for (const [id, waiter] of batch) {
                const value = catalog.held.get(id) ?? null;
                catalog.held.set(id, value);
                waiter(value);
            }
        } catch (error) {
            console.warn('Reference catalog read failed', error);
            for (const [id, waiter] of batch) {
                catalog.held.set(id, null);
                waiter(null);
            }
        }
    }
}

function request(id: string): Promise<Definition | null> {
    const catalog = pageCatalog();
    const pending = catalog.pending.get(id);
    if (pending) return pending;
    // Misses and transport failures are sticky for this page too. Opening or
    // repainting the same card must not keep retrying an unavailable identity.
    if (catalog.held.has(id)) return Promise.resolve(catalog.held.get(id)!);
    const job = new Promise<Definition | null>(resolve => {
        if (!catalog.queue.size) setTimeout(() => { void flush(catalog); }, 0);
        catalog.queue.set(id, resolve);
    }).finally(() => { catalog.pending.delete(id); });
    catalog.pending.set(id, job);
    return job;
}

/** Inventory the renderer's reference identities, including delegated controls,
 * ordinary item links and text-only labels, without rereading layer shapes. */
const referenceSelector = '[onclick*="openItemInfo"], [data-ft-ld-action="open-info"], a[href*="item.php?id="], [data-ft-reference-id]';

function referenceLinks(container: HTMLElement): { element: HTMLElement; id: string; key: string }[] {
    const links = [];
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    for (const element of container.querySelectorAll<HTMLElement>(referenceSelector)) {
        const onclick = element.getAttribute('onclick') ?? '';
        const href = element.getAttribute('href');
        const id = element.getAttribute('data-ft-reference-id') ?? element.getAttribute('data-ft-item-id')
            ?? /\bopenItemInfo\(['"]([^'"]+)['"]\)/i.exec(onclick)?.[1]
            ?? (href ? new URL(href, document.baseURI).searchParams.get('id') : null);
        if (id && uuid.test(id)) links.push({ element, id: id.toUpperCase(),
            key: `${element.tagName}:${element.dataset.ftReferenceLayer ?? ''}:${id.toUpperCase()}` });
    }
    return links;
}

const paints = new WeakMap<HTMLElement, object>();

/** Paint first, then fill only reference rows. An overtaken/closed card never
 * receives an old completion; unrelated choice controls retain their DOM. */
export function hydrateReferenceRows(container: HTMLElement, html: (definitions: Definition) => string): void {
    const paint = {};
    paints.set(container, paint);
    // A named shell is sufficient: only the renderer's explicit missing-name
    // markers owe a read. Nested icons must not make their named parent due.
    const links = referenceLinks(container).filter(link => [...link.element.querySelectorAll('.ft-ld-row-unresolved')]
        .some(marker => marker.closest(referenceSelector) === link.element));
    const missing = [...new Set(links.map(link => link.id))];
    if (!missing.length) return;
    void Promise.all(missing.map(async id => [id, await request(id)] as const)).then(entries => {
        if (paints.get(container) !== paint || !container.isConnected || !links.some(link => container.contains(link.element))) return;
        const found = new Map(entries.filter((entry): entry is readonly [string, Definition] => entry[1] !== null));
        if (!found.size) return;
        const next = document.createElement('div');
        next.innerHTML = html(Object.fromEntries(found));
        const replacements = new Map<string, HTMLElement>();
        for (const link of referenceLinks(next)) {
            replacements.set(link.key, link.element);
        }
        for (const link of links) {
            const replacement = replacements.get(link.key);
            if (!found.has(link.id) || !container.contains(link.element)) continue;
            if (!replacement) continue;
            link.element.replaceWith(replacement.cloneNode(true));
        }
        for (const mount of [window.ftMountIcons, window.ftMountRuneIcons, window.ftMountThumbnails]) mount?.(container);
    }).catch(error => { console.warn('Reference rows remain unresolved', error); });
}
